import React, { useState } from 'react';
import { Download, Printer, Copy, RefreshCw, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { LoadingSpinner } from '../ui/loading-states';
import { useLocationQR, downloadQR, printQR } from '../../hooks/locations/useLocationQR';
import type { Location } from '../../types/location.types';

interface QRCodeModalProps {
  location: Location;
  isOpen: boolean;
  onClose: () => void;
}

export const QRCodeModal: React.FC<QRCodeModalProps> = ({
  location,
  isOpen,
  onClose,
}) => {
  const { data: qrData, isLoading, refetch, isRefetching } = useLocationQR(location.id);
  const [copiedDeeplink, setCopiedDeeplink] = useState(false);
  const [downloadingFormat, setDownloadingFormat] = useState<string | null>(null);

  const handleCopyDeeplink = async () => {
    if (qrData?.deeplink) {
      await navigator.clipboard.writeText(qrData.deeplink);
      setCopiedDeeplink(true);
      setTimeout(() => setCopiedDeeplink(false), 2000);
    }
  };

  const handleDownload = async (format: 'svg' | 'png', size = 512) => {
    setDownloadingFormat(`${format}-${size}`);
    try {
      await downloadQR(location.id, location.name, format, size);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloadingFormat(null);
    }
  };

  const handlePrint = async () => {
    try {
      await printQR(location.id, location.name);
    } catch (error) {
      console.error('Print failed:', error);
    }
  };

  const handleRegenerate = async () => {
    await refetch();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>QR код локации</DialogTitle>
          <DialogDescription>{location.name}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center space-y-4">
          {/* QR код */}
          {isLoading ? (
            <div className="flex items-center justify-center h-64 w-64">
              <LoadingSpinner />
            </div>
          ) : qrData ? (
            <div className="border-2 border-gray-200 rounded-lg p-4 bg-white">
              <div
                className="w-64 h-64"
                dangerouslySetInnerHTML={{ __html: qrData.svg }}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 w-64 border-2 border-gray-200 rounded-lg">
              <p className="text-gray-500">QR код не найден</p>
            </div>
          )}

          {/* Информация */}
          <div className="text-center space-y-1">
            <p className="text-sm text-gray-600">ID локации: {location.id}</p>
            <p className="text-sm text-gray-600">
              Радиус действия: {location.radius_meters} м
            </p>
            {location.requires_gps_validation && (
              <p className="text-sm text-green-600">GPS проверка включена</p>
            )}
          </div>

          {/* Действия */}
          <div className="grid grid-cols-2 gap-2 w-full">
            {/* Скачать PNG */}
            <div className="relative group">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => handleDownload('png', 256)}
                disabled={!qrData || downloadingFormat === 'png-256'}
              >
                {downloadingFormat === 'png-256' ? (
                  <LoadingSpinner className="mr-2 h-4 w-4" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                PNG 256px
              </Button>
              <div className="absolute top-full mt-1 hidden group-hover:flex gap-1">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleDownload('png', 512)}
                  disabled={!qrData || downloadingFormat === 'png-512'}
                >
                  512px
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleDownload('png', 1024)}
                  disabled={!qrData || downloadingFormat === 'png-1024'}
                >
                  1024px
                </Button>
              </div>
            </div>

            {/* Скачать SVG */}
            <Button
              variant="outline"
              onClick={() => handleDownload('svg')}
              disabled={!qrData || downloadingFormat === 'svg-512'}
            >
              {downloadingFormat === 'svg-512' ? (
                <LoadingSpinner className="mr-2 h-4 w-4" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              SVG
            </Button>

            {/* Печать */}
            <Button variant="outline" onClick={handlePrint} disabled={!qrData}>
              <Printer className="mr-2 h-4 w-4" />
              Печать
            </Button>

            {/* Копировать deeplink */}
            <Button
              variant="outline"
              onClick={handleCopyDeeplink}
              disabled={!qrData?.deeplink}
            >
              {copiedDeeplink ? (
                <>
                  <Check className="mr-2 h-4 w-4 text-green-500" />
                  Скопировано
                </>
              ) : (
                <>
                  <Copy className="mr-2 h-4 w-4" />
                  Deeplink
                </>
              )}
            </Button>
          </div>

          {/* Регенерировать */}
          <Button
            variant="secondary"
            className="w-full"
            onClick={handleRegenerate}
            disabled={isRefetching}
          >
            {isRefetching ? (
              <LoadingSpinner className="mr-2 h-4 w-4" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Обновить QR код
          </Button>

          {/* Инструкция */}
          <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800">
            <p className="font-medium mb-1">Как использовать:</p>
            <ol className="space-y-1 text-xs">
              <li>1. Распечатайте или сохраните QR код</li>
              <li>2. Разместите на входе в локацию</li>
              <li>3. Сотрудники сканируют код в мобильном приложении</li>
              <li>4. Система автоматически фиксирует вход/выход</li>
            </ol>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};